import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { api } from '../../lib/useEntity';
import { PageHeader } from '../../components/ui/PageHeader';
import { Button } from '../../components/ui/Button';
import { Select } from '../../components/ui/Select';
import { Input } from '../../components/ui/Input';
import { DataTable, type Column, type DataTableFilter } from '../../components/DataTable';
import { fmt } from '../../lib/formatters';
import { toast } from '../../lib/toast';

type Semana = {
  key: string;
  fecha_informe: string;
  semana_inicio: string;
  semana_fin: string;
};

type HistoricoRow = {
  id: string;
  sku: string;
  ean: string | null;
  nombre: string;
  estado: string;
  gama_id: string | null;
  gama_nombre: string | null;
  categoria_id: string | null;
  categoria_nombre: string | null;
  subcategoria_id: string | null;
  subcategoria_nombre: string | null;
  detalle_nombre: string | null;
  estado_nombre: string | null;
  estado_color: string | null;
  ventas: Record<string, number | null>;
  media: number;
  total: number;
};

type ApiResp = { semanas: Semana[]; data: HistoricoRow[] };

type Props = {
  clienteCodigo?: string;
  title?: string;
  subtitle?: string;
};

/** Pares únicos {id, nombre} presentes en las filas, ordenados alfabéticamente. */
function uniqueOptions(
  rows: HistoricoRow[] | undefined,
  idKey: keyof HistoricoRow,
  nameKey: keyof HistoricoRow,
): { value: string; label: string }[] {
  if (!rows) return [];
  const seen = new Map<string, string>();
  for (const r of rows) {
    const id = r[idKey];
    const nm = r[nameKey];
    if (typeof id === 'string' && id && typeof nm === 'string' && nm) {
      if (!seen.has(id)) seen.set(id, nm);
    }
  }
  return Array.from(seen.entries())
    .map(([value, label]) => ({ value, label }))
    .sort((a, b) => a.label.localeCompare(b.label, 'es'));
}

/**
 * Clase de color de una celda de venta semanal según la spec del CEO:
 *  - máximo de la fila → verde + negrita
 *  - mínimo de la fila → rojo
 *  - por encima de la media → verde
 *  - por debajo de la media → amarillo
 *  - igual a la media (o sin variación) → neutro
 * Mín/máx tienen prioridad y solo se aplican si hay variación (max !== min).
 */
function cellClass(v: number | null, media: number, min: number | null, max: number | null): string {
  if (v == null || !Number.isFinite(v)) return '';
  if (min != null && max != null && max !== min) {
    if (v === max) return 'bg-emerald-200 font-bold text-emerald-900';
    if (v === min) return 'bg-rose-100 font-semibold text-rose-700';
  }
  if (v > media) return 'bg-emerald-100 text-emerald-800';
  if (v < media) return 'bg-amber-100 text-amber-800';
  return '';
}

export function HistoricoVentas({ clienteCodigo, title, subtitle }: Props = {}) {
  // El periodo se elige en UNO de dos modos excluyentes:
  //  - 'semanas': las últimas N semanas con datos (4 / 8 / 12 / 24).
  //  - 'rango':  un rango de fechas (sobre la fecha del fichero importado).
  const [modo, setModo] = useState<'semanas' | 'rango'>('semanas');
  const [desde, setDesde] = useState('');
  const [hasta, setHasta] = useState('');
  const [semanas, setSemanas] = useState('8');

  const usaRango = modo === 'rango';

  const params = useMemo(() => {
    const p = new URLSearchParams();
    if (clienteCodigo) p.set('cliente_codigo', clienteCodigo);
    if (usaRango) {
      if (desde) p.set('desde', desde);
      if (hasta) p.set('hasta', hasta);
    } else {
      p.set('semanas', semanas);
    }
    return p.toString();
  }, [clienteCodigo, usaRango, desde, hasta, semanas]);

  const historico = useQuery({
    queryKey: ['historico-ventas', clienteCodigo ?? '*', usaRango ? `rango:${desde}:${hasta}` : `semanas:${semanas}`],
    queryFn: () => api.get<ApiResp>(`/productos/historico-ventas?${params}`),
  });

  const estados = useQuery({
    queryKey: ['list', '/admin/producto-estados'],
    queryFn: () => api.get<{ data: { codigo: string; nombre: string }[] }>('/admin/producto-estados'),
  });

  const semanasArr = useMemo(() => historico.data?.semanas ?? [], [historico.data]);
  const rows = historico.data?.data;

  // Filas tal cual se ven en la tabla (búsqueda + filtros) para exportar.
  const [vistaRows, setVistaRows] = useState<HistoricoRow[]>([]);
  const [exporting, setExporting] = useState(false);

  const gamaOptions = useMemo(() => uniqueOptions(rows, 'gama_id', 'gama_nombre'), [rows]);
  const categoriaOptions = useMemo(() => uniqueOptions(rows, 'categoria_id', 'categoria_nombre'), [rows]);
  const subcategoriaOptions = useMemo(() => uniqueOptions(rows, 'subcategoria_id', 'subcategoria_nombre'), [rows]);
  const estadoOptions = useMemo(() => {
    const catalogo = new Map<string, string>((estados.data?.data ?? []).map((e) => [e.codigo, e.nombre]));
    const seen = new Set<string>();
    for (const r of rows ?? []) if (r.estado) seen.add(r.estado);
    return Array.from(seen)
      .map((codigo) => ({ value: codigo, label: catalogo.get(codigo) ?? codigo }))
      .sort((a, b) => a.label.localeCompare(b.label, 'es'));
  }, [rows, estados.data]);

  const columns = useMemo<Column<HistoricoRow>[]>(() => {
    const cols: Column<HistoricoRow>[] = [
      {
        key: 'sku',
        label: 'SKU',
        render: (r) => <span className="font-mono text-xs">{r.sku}</span>,
        sortValue: (r) => r.sku,
        width: '7rem',
      },
      {
        key: 'nombre',
        label: 'Nombre',
        render: (r) => (
          <span className="block max-w-[16rem] truncate text-sm" title={r.nombre}>{r.nombre}</span>
        ),
        sortValue: (r) => r.nombre,
      },
      { key: 'gama_nombre', label: 'Gama', render: (r) => r.gama_nombre ?? '—', sortValue: (r) => r.gama_nombre ?? '' },
      { key: 'subcategoria_nombre', label: 'Subcategoría', render: (r) => r.subcategoria_nombre ?? '—', sortValue: (r) => r.subcategoria_nombre ?? '' },
      { key: 'detalle_nombre', label: 'Detalle', render: (r) => r.detalle_nombre ?? '—', sortValue: (r) => r.detalle_nombre ?? '' },
      {
        key: 'media',
        label: 'Media semanal',
        align: 'right',
        render: (r) => <span className="font-semibold text-slate-700">{fmt.number(Number(r.media.toFixed(2)))}</span>,
        sortValue: (r) => r.media,
      },
    ];

    for (const s of semanasArr) {
      cols.push({
        key: s.key,
        label: fmt.date(s.fecha_informe),
        align: 'right',
        render: (r) => {
          const v = r.ventas[s.key];
          if (v == null || !Number.isFinite(v)) return <span className="text-slate-300">—</span>;
          const vals = semanasArr
            .map((x) => r.ventas[x.key])
            .filter((x): x is number => x != null && Number.isFinite(x));
          const min = vals.length ? Math.min(...vals) : null;
          const max = vals.length ? Math.max(...vals) : null;
          const cls = cellClass(v, r.media, min, max);
          return <span className={`inline-block rounded px-1.5 py-0.5 ${cls}`}>{fmt.int(v)}</span>;
        },
        sortValue: (r) => {
          const v = r.ventas[s.key];
          return v == null ? null : v;
        },
      });
    }

    cols.push({
      key: 'total',
      label: 'Total acumulado',
      align: 'right',
      render: (r) => <span className="font-bold text-slate-900">{fmt.int(r.total)}</span>,
      sortValue: (r) => r.total,
    });
    return cols;
  }, [semanasArr]);

  // Memoizado para estabilizar su identidad entre renders (ver DataTable).
  const filtros = useMemo<DataTableFilter<HistoricoRow>[]>(() => [
    { key: 'gama_id', label: 'Gama', options: gamaOptions },
    { key: 'categoria_id', label: 'Categoría', options: categoriaOptions },
    { key: 'subcategoria_id', label: 'Subcategoría', options: subcategoriaOptions },
    { key: 'estado', label: 'Estado', options: estadoOptions },
  ], [gamaOptions, categoriaOptions, subcategoriaOptions, estadoOptions]);

  const exportarExcel = async () => {
    const fuente = vistaRows.length > 0 ? vistaRows : (rows ?? []);
    if (fuente.length === 0 || semanasArr.length === 0) {
      toast.info('No hay datos que exportar');
      return;
    }
    setExporting(true);
    try {
      const payloadRows = fuente.map((r) => ({
        sku: r.sku,
        nombre: r.nombre,
        gama: r.gama_nombre,
        subcategoria: r.subcategoria_nombre,
        detalle: r.detalle_nombre,
        media: r.media,
        total: r.total,
        ventas: r.ventas,
      }));
      const { data: sess } = await (await import('../../lib/supabase')).supabase.auth.getSession();
      const token = sess.session?.access_token;
      const resp = await fetch('/api/productos/historico-ventas/export-excel', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) },
        body: JSON.stringify({ semanas: semanasArr, rows: payloadRows }),
      });
      if (!resp.ok) throw new Error(`Error ${resp.status}`);
      const blob = await resp.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `historico_ventas_${new Date().toISOString().slice(0, 10)}.xlsx`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      toast.error('No se pudo generar el Excel', (err as Error).message);
    } finally {
      setExporting(false);
    }
  };

  return (
    <div>
      <PageHeader
        title={title ?? 'Histórico Ventas'}
        subtitle={subtitle ?? 'Ventas semanales por producto. Los títulos de cada semana son la fecha del fichero importado.'}
        actions={
          <Button
            variant="secondary"
            onClick={exportarExcel}
            disabled={exporting}
            title="Descarga un Excel con el histórico tal como está filtrado en la tabla, con el mismo coloreado."
          >
            {exporting ? 'Generando…' : '📊 Exportar Excel'}
          </Button>
        }
      />

      <DataTable<HistoricoRow>
        columns={columns}
        rows={rows}
        loading={historico.isLoading}
        rowKey={(r) => r.id}
        onFilteredRowsChange={setVistaRows}
        searchPlaceholder="Buscar por SKU, EAN o nombre…"
        stickyHeader
        empty={
          <div className="px-4 py-8 text-center text-sm text-slate-500">
            No hay ventas en el periodo seleccionado.
          </div>
        }
        filters={filtros}
        filterExtras={
          <div className="flex flex-wrap items-center gap-2">
            <label className="flex items-center gap-1 text-xs text-slate-500">
              <span className="whitespace-nowrap">Periodo:</span>
              <Select
                emptyLabel={null}
                options={[
                  { value: 'semanas', label: 'Últimas semanas' },
                  { value: 'rango', label: 'Rango de fechas' },
                ]}
                value={modo}
                onChange={(e) => setModo(e.target.value as 'semanas' | 'rango')}
                className="w-40"
              />
            </label>

            {modo === 'semanas' ? (
              <label className="flex items-center gap-1 text-xs text-slate-500">
                <span>Últimas</span>
                <Select
                  emptyLabel={null}
                  options={[
                    { value: '4', label: '4' },
                    { value: '8', label: '8' },
                    { value: '12', label: '12' },
                    { value: '24', label: '24' },
                  ]}
                  value={semanas}
                  onChange={(e) => setSemanas(e.target.value)}
                  className="w-16"
                />
                <span>semanas</span>
              </label>
            ) : (
              <>
                <label className="flex items-center gap-1 text-xs text-slate-500">
                  <span>Desde:</span>
                  <Input type="date" value={desde} max={hasta || undefined} onChange={(e) => setDesde(e.target.value)} className="w-36" />
                </label>
                <label className="flex items-center gap-1 text-xs text-slate-500">
                  <span>Hasta:</span>
                  <Input type="date" value={hasta} min={desde || undefined} onChange={(e) => setHasta(e.target.value)} className="w-36" />
                </label>
              </>
            )}

            {semanasArr.length > 0 && (
              <span className="rounded-md border border-slate-200 bg-slate-50 px-2 py-1 text-xs text-slate-600">
                {semanasArr.length} semana{semanasArr.length === 1 ? '' : 's'} · {fmt.date(semanasArr[0].fecha_informe)} → {fmt.date(semanasArr[semanasArr.length - 1].fecha_informe)}
              </span>
            )}
          </div>
        }
      />

      {/* Leyenda de colores */}
      <div className="mt-2 flex flex-wrap items-center gap-3 text-[11px] text-slate-500">
        <span className="inline-flex items-center gap-1"><span className="inline-block h-3 w-3 rounded bg-emerald-200" /> Máximo (semana con más ventas)</span>
        <span className="inline-flex items-center gap-1"><span className="inline-block h-3 w-3 rounded bg-emerald-100" /> Por encima de la media</span>
        <span className="inline-flex items-center gap-1"><span className="inline-block h-3 w-3 rounded bg-amber-100" /> Por debajo de la media</span>
        <span className="inline-flex items-center gap-1"><span className="inline-block h-3 w-3 rounded bg-rose-100" /> Mínimo (semana con menos ventas)</span>
      </div>
    </div>
  );
}
